import { LayoutDashboard, Map, Users, Megaphone, FileText, Headphones } from "lucide-react";
import { Eyebrow, Reveal } from "./utils";
import ActivityCards from "./ActivityCards";

const nav = [
  { icon: LayoutDashboard, label: "Overview", active: true },
  { icon: Map, label: "Revenue Roadmap" },
  { icon: Users, label: "Leads & Pipeline" },
  { icon: Megaphone, label: "Campaigns" },
  { icon: FileText, label: "Documents" },
  { icon: Headphones, label: "Support & Calls" },
];

const stats = [
  { label: "Revenue Goal Progress", value: "On Track", bar: 68 },
  { label: "New Leads", value: "42", bar: 80 },
  { label: "Quotes Sent", value: "18", bar: 54 },
  { label: "Jobs Booked", value: "11", bar: 44 },
];

function Dashboard() {
  return (
    <div className="relative [perspective:1800px]">
      <div
        className="relative preserve-3d rounded-2xl bg-brand-charcoal border border-white/10 p-2.5 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.8)]"
        style={{ transform: "rotateX(6deg)" }}
      >
        <div className="rounded-xl bg-[#161616] overflow-hidden border border-white/5">
          {/* top bar */}
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-brand-gold/50" />
            <span className="ml-3 font-mono text-[10px] tracking-wide text-white/30">portal.apexflowai.co</span>
          </div>

          <div className="grid grid-cols-[140px_1fr] min-h-[360px]">
            {/* sidebar */}
            <div className="hidden sm:block border-r border-white/5 p-3 space-y-1">
              {nav.map((n) => (
                <div
                  key={n.label}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 ${
                    n.active ? "bg-brand-gold/12 text-brand-gold" : "text-white/45"
                  }`}
                >
                  <n.icon className="h-3.5 w-3.5" strokeWidth={1.6} />
                  <span className="font-sans text-[11px] font-medium truncate">{n.label}</span>
                </div>
              ))}
            </div>

            {/* main */}
            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-2 gap-3">
                {stats.map((s) => (
                  <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/5 p-3.5">
                    <p className="font-sans text-[10px] uppercase tracking-wider text-white/40">{s.label}</p>
                    <p className="mt-1.5 font-serif text-2xl text-white leading-none">{s.value}</p>
                    <div className="mt-3 h-1 rounded-full bg-white/8 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-brand-gold to-brand-gold-deep" style={{ width: `${s.bar}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 space-y-2.5">
                <div className="rounded-xl border border-brand-gold/25 bg-brand-gold/8 p-3.5">
                  <p className="font-sans text-[10px] uppercase tracking-wider text-brand-gold">Current Top Opportunity</p>
                  <p className="mt-1 font-sans text-sm font-semibold text-white">Quote Recovery</p>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3.5">
                  <p className="font-sans text-[10px] uppercase tracking-wider text-white/40">Active Initiative</p>
                  <p className="mt-1 font-sans text-sm font-medium text-white/90">Quote Follow-Up System</p>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/[0.03] border border-white/5 p-3.5">
                  <div>
                    <p className="font-sans text-[10px] uppercase tracking-wider text-white/40">Awaiting Approval</p>
                    <p className="mt-1 font-sans text-sm font-medium text-white/90">Past Customer Reactivation Campaign</p>
                  </div>
                  <span className="rounded-full bg-amber-400/15 px-2.5 py-1 font-sans text-[10px] font-semibold text-amber-300">Review</span>
                </div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3.5">
                  <p className="font-sans text-[10px] uppercase tracking-wider text-white/40">Next Best Opportunity</p>
                  <p className="mt-1 font-sans text-sm font-medium text-white/90">Referral & Review Growth</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientPortalPreview() {
  return (
    <section data-testid="portal-preview-section" className="relative overflow-hidden bg-brand-black py-24 md:py-32">
      <div className="grain-overlay pointer-events-none absolute inset-0 opacity-[0.04]" />
      <div className="pointer-events-none absolute -right-20 top-1/4 h-[440px] w-[440px] rounded-full bg-brand-gold/10 blur-[130px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-16 items-center">
        <div>
          <Reveal><Eyebrow>Visibility Without the Complexity</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-white leading-[1.08]">
              Finally Understand What Is Happening With Your <span className="gold-text">Growth.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <div className="mt-7 space-y-4 font-sans text-base md:text-lg leading-relaxed text-white/60">
              <p>
                Apex Flow clients do not need to manage complicated automations, campaign systems, reporting logic, or
                pipeline structures themselves.
              </p>
              <p>
                The Apex Flow Client Portal gives business owners a clear view of progress, priorities, approvals, and
                next actions—while Apex Flow manages execution and optimization behind the scenes.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={1}>
          <Dashboard />
        </Reveal>
      </div>

      {/* relocated live-activity cards — clean full-width product strip */}
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 mt-12 md:mt-16">
        <Reveal>
          <div className="flex items-center gap-3">
            <span className="h-px w-7 bg-brand-gold/60" />
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.22em] text-brand-gold">Live Activity Feed</p>
          </div>
        </Reveal>
        <Reveal delay={1} className="mt-6">
          <ActivityCards className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch" />
        </Reveal>
      </div>
    </section>
  );
}
